import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Trash2, Play, Pause, Clock, AlertCircle, Eye, Send, X, Mail, MessageSquare, Pencil, History } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getAIScheduledTasks,
  previewAIScheduledTask,
  runAIScheduledTaskNow,
  setAIScheduledTaskActive,
  deleteAIScheduledTask
} from '../../utils/api';

/**
 * Management surface for AI scheduled tasks — the recurring email reports
 * users create conversationally in Lodgeic AI ("email me a pickup report
 * every Monday at 7am"). Creation and config edits stay in the assistant;
 * this list covers the lifecycle: preview, run now, pause/resume, delete.
 */
const AIScheduledTasksManagement = ({ currentUser, refreshTrigger }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [preview, setPreview] = useState(null); // { taskId, name, subject, html } | { taskId, loading: true }
  const [historyTask, setHistoryTask] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAIScheduledTasks();
      setTasks(data);
    } catch (err) {
      console.error('Error fetching AI scheduled tasks:', err);
      setError('Failed to load scheduled tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.email) fetchTasks();
  }, [currentUser?.email, refreshTrigger, fetchTasks]);

  const withActionLoading = async (key, fn) => {
    try {
      setActionLoading(prev => ({ ...prev, [key]: true }));
      await fn();
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleToggleActive = (task) => withActionLoading(`toggle-${task.id}`, async () => {
    try {
      await setAIScheduledTaskActive(task.id, task.isActive === false);
      await fetchTasks();
    } catch (err) {
      console.error('Error toggling scheduled task:', err);
      toast.error('Failed to update the task status.');
    }
  });

  const handleDelete = (task) => {
    if (!window.confirm(`Delete "${task.name}"? It will stop running and sending emails.`)) return;
    withActionLoading(`delete-${task.id}`, async () => {
      try {
        await deleteAIScheduledTask(task.id);
        await fetchTasks();
        toast.success('Scheduled task deleted');
      } catch (err) {
        console.error('Error deleting scheduled task:', err);
        toast.error('Failed to delete the task.');
      }
    });
  };

  const handleRunNow = (task) => withActionLoading(`run-${task.id}`, async () => {
    try {
      const result = await runAIScheduledTaskNow(task.id);
      if (result?.skipped) {
        toast('The task ran but its no-change condition skipped the email.', { icon: 'ℹ️' });
      } else if (result?.emailSent) {
        toast.success('Run completed — test email sent to the configured recipients');
      } else {
        toast.success('Run completed');
      }
    } catch (err) {
      console.error('Error running scheduled task:', err);
      const reason = err?.response?.data?.error || err?.message;
      toast.error(`The task run failed${reason ? `: ${reason}` : '.'}`);
    } finally {
      // Success and failure both change durable run history. Refresh so a
      // reviewer never has to reload the page to see what just happened.
      await fetchTasks();
    }
  });

  const handlePreview = async (task) => {
    setPreview({ taskId: task.id, name: task.name, loading: true });
    try {
      const data = await previewAIScheduledTask(task.id);
      setPreview({ taskId: task.id, name: task.name, subject: data.subject, html: data.html });
    } catch (err) {
      console.error('Error previewing scheduled task:', err);
      setPreview(null);
      toast.error('Failed to render the preview.');
    }
  };

  const handleEdit = (task) => {
    window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', { detail: {
      expanded: true,
      hotelId: String(task.hotelId),
      prompt: `Edit my automation "${task.name}" (id ${task.id}). Show me its current configuration before changing it.`
    } }));
  };

  const describeSchedule = (task) => {
    if (task.workflow?.eventTrigger) return `When ${task.workflow.eventTrigger.type.replaceAll('_', ' ')} matches ${task.workflow.eventTrigger.patternKey}`;
    const tt = task.triggerTime || {};
    const days = Array.isArray(tt.daysOfWeek) && tt.daysOfWeek.length
      ? tt.daysOfWeek.map(d => d.slice(0, 3)).join(', ')
      : null;
    const base = tt.frequency === 'weekly' ? `Weekly${days ? ` · ${days}` : ''}` : 'Daily';
    return `${base} at ${tt.time || '--:--'}${tt.timezone ? ` (${tt.timezone})` : ''}`;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const describeRecipients = (task) => {
    const recipients = task.recipients || [];
    if (recipients.length === 0) return 'No recipients';
    if (recipients.length === 1) return recipients[0];
    return `${recipients[0]} +${recipients.length - 1}`;
  };

  const getStatusBadge = (task) => {
    if (task.isDeleted) {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Deleted</span>;
    }
    if (task.isActive === false) {
      return (
        <span
          className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
          title={task.disabledReason || undefined}
        >
          Paused
        </span>
      );
    }
    return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Active</span>;
  };

  const deletedCount = tasks.filter(task => task.isDeleted).length;
  const visibleTasks = showDeleted ? tasks : tasks.filter(task => !task.isDeleted);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading scheduled tasks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Scheduled Tasks</h2>
          <p className="text-gray-600">
            Recurring reports created in Lodgeic AI — pause, preview, or run them from here
          </p>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', { detail: { expanded: true } }))}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ background: 'var(--lodge-orange-500, #f97316)' }}
        >
          <Sparkles size={16} />
          Create in Lodgeic AI
        </button>
      </div>

      {deletedCount > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowDeleted(value => !value)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showDeleted ? 'Hide deleted' : `Show deleted (${deletedCount})`}
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle size={20} className="text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            ×
          </button>
        </div>
      )}

      {/* Task List */}
      {visibleTasks.length === 0 ? (
        <div className="text-center py-12">
          <Sparkles size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No scheduled tasks yet</h3>
          <p className="text-gray-600 mb-4 max-w-md mx-auto">
            Ask Lodgeic AI for a recurring report — for example, "email me a pickup report every
            Monday at 7am" — and it will appear here.
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', { detail: { expanded: true } }))}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--lodge-orange-500, #f97316)' }}
          >
            <Sparkles size={16} />
            Open Lodgeic AI
          </button>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last / Next Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{task.name}</div>
                      {task.description && (
                        <div className="text-sm text-gray-500 max-w-xs truncate" title={task.description}>
                          {task.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(task)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock size={16} className="mr-2 text-gray-400 shrink-0" />
                        {describeSchedule(task)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="flex items-center text-sm text-gray-900"
                        title={(task.recipients || []).join(', ')}
                      >
                        {(task.deliveryChannels || ['email']).includes('push') ? (
                          <MessageSquare size={16} className="mr-2 text-gray-400 shrink-0" />
                        ) : (
                          <Mail size={16} className="mr-2 text-gray-400 shrink-0" />
                        )}
                        {describeRecipients(task)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(task.executionHistory?.lastExecuted || task.lastExecuted)}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {task.isDeleted ? `Deleted: ${formatDate(task.deletedAt)}` : task.isActive === false ? 'Paused' : `Next: ${formatDate(task.nextScheduled)}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {!task.isDeleted && (
                          <button
                            onClick={() => handleEdit(task)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                            title="Edit in Lodgeic AI"
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => setHistoryTask(task)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                          title="Run history"
                        >
                          <History size={16} />
                        </button>
                        {!task.isDeleted && <>
                          <button
                            onClick={() => handlePreview(task)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                            title="Preview the email"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleRunNow(task)}
                            disabled={actionLoading[`run-${task.id}`]}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title={task.executionMode === 'workflow' ? 'Run now (notification rules apply)' : 'Send a test email now'}
                          >
                            <Send size={16} className={actionLoading[`run-${task.id}`] ? 'animate-pulse' : ''} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(task)}
                            disabled={actionLoading[`toggle-${task.id}`]}
                            className={`p-2 rounded ${
                              task.isActive !== false
                                ? 'text-yellow-600 hover:bg-yellow-50'
                                : 'text-green-600 hover:bg-green-50'
                            } transition-colors disabled:opacity-50`}
                            title={task.isActive !== false ? 'Pause' : 'Resume'}
                          >
                            {task.isActive !== false ? <Pause size={16} /> : <Play size={16} />}
                          </button>
                          <button
                            onClick={() => handleDelete(task)}
                            disabled={actionLoading[`delete-${task.id}`]}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{preview.name}</div>
                {preview.subject && (
                  <div className="text-xs text-gray-500 truncate">Subject: {preview.subject}</div>
                )}
              </div>
              <button
                onClick={() => setPreview(null)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                aria-label="Close preview"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 min-h-[300px] bg-gray-50">
              {preview.loading ? (
                <div className="flex items-center justify-center h-72 text-gray-500 text-sm">
                  Rendering preview…
                </div>
              ) : (
                <iframe
                  title="Scheduled task email preview"
                  srcDoc={preview.html}
                  sandbox=""
                  className="w-full h-[70vh] border-0 bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {historyTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <div className="font-semibold">{historyTask.name}</div>
                <div className="text-xs text-gray-500">Run history and audit trail</div>
              </div>
              <button aria-label="Close history" onClick={() => setHistoryTask(null)}><X size={18} /></button>
            </div>
            <div className="p-5 overflow-auto max-h-[68vh] space-y-5">
              {(historyTask.runHistory || []).length === 0 ? <p className="text-gray-500">No runs yet.</p> : (
                <div className="space-y-2">
                  {[...(historyTask.runHistory || [])].reverse().map((run, i) => (
                    <div key={`${run.at}-${i}`} className={`border rounded-lg p-3 text-sm ${run.failed ? 'border-red-200 bg-red-50' : ''}`}>
                      <div className={`font-medium ${run.failed ? 'text-red-800' : ''}`}>{new Date(run.at).toLocaleString()} · {run.failed ? 'Failed' : run.skipped ? 'No notification' : run.emailSent ? 'Email sent' : 'Completed'}</div>
                      <div className={run.failed ? 'text-red-700' : 'text-gray-500'}>
                        {run.failed ? run.reason : `${run.proposalCount ?? 0} proposals${run.reason ? ` · ${run.reason}` : ''}`}
                      </div>
                      {run.workflowRunId && <a className="text-blue-600" href={`/workflow-runs/${encodeURIComponent(run.workflowRunId)}`}>Review run</a>}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="font-semibold mb-2">Audit</div>
                <div className="space-y-1 text-sm text-gray-600">
                  {[...(historyTask.auditTrail || [])].reverse().map((entry, i) => (
                    <div key={`${entry.at}-${i}`}>{new Date(entry.at).toLocaleString()} · {entry.action} · {entry.actor}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIScheduledTasksManagement;
